import { createTheme } from "@mui/material/styles";
import { PORTAL } from "./portals.js";
import { muiThemeV2 } from "./muiTheme.js";
import { colorsFoundation } from "./tokens/colors-foundation.js";
import { componentTokens } from "./tokens/components.js";

const sidebarPalettes = {
  [PORTAL.SCHADENPORTAL]: {
    card: {
      headerBackground: colorsFoundation.neutral[100],
    },
    background: {
      sidebar: colorsFoundation.neutral[50],
    },
    sidebarNav: {
      text: {
        default: colorsFoundation.neutral[700],
        hover: colorsFoundation.neutral[900],
        active: colorsFoundation.key.blue[700],
      },
      icon: {
        default: colorsFoundation.neutral[700],
        hover: colorsFoundation.neutral[900],
        active: colorsFoundation.key.blue[700],
      },
      background: {
        default: colorsFoundation.neutral[50],
        hover: colorsFoundation.neutral[100],
        active: colorsFoundation.neutral[200],
      },
    },
  },
  [PORTAL.KUNDENPORTAL]: {
    card: {
      headerBackground: colorsFoundation.neutral[50],
    },
    background: {
      sidebar: colorsFoundation.key.blue[700],
    },
    sidebarNav: {
      text: {
        default: colorsFoundation.key.blue[50],
        hover: colorsFoundation.neutral.white,
        active: colorsFoundation.neutral.white,
      },
      icon: {
        default: colorsFoundation.key.blue[50],
        hover: colorsFoundation.neutral.white,
        active: colorsFoundation.neutral.white,
      },
      background: {
        default: colorsFoundation.key.blue[700],
        hover: colorsFoundation.key.blue[600],
        active: colorsFoundation.key.blue[500],
      },
    },
  },
};

const flattenPaths = (value, prefix = "") =>
  Object.entries(value).flatMap(([key, val]) => {
    const key_path = prefix ? `${prefix}.${key}` : key;
    return val && typeof val === "object"
      ? flattenPaths(val, key_path)
      : [key_path];
  });

/**
 * The only palette paths the two portal themes may differ in. Anything else
 * fails the test in FE/packages/fe-shared/tests/theme/portalTheme.test.js.
 */
export const PORTAL_PALETTE_DIFF_PATHS = flattenPaths(
  sidebarPalettes[PORTAL.KUNDENPORTAL]
);

/**
 * Interaction states from the click dummy. Kundenportal only, so the
 * Schadenportal keeps the appearance it has.
 */
const interactionComponents = {
  MuiTableRow: {
    styleOverrides: {
      root: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: componentTokens.dataTable.row.hoverBackground,
        },
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        textDecoration: "none",
        cursor: "pointer",
        "&:hover": {
          color: colorsFoundation.key.blue[500],
          textDecoration: "none",
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      containedPrimary: {
        "&:hover": {
          opacity: 0.7,
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        "&:hover": {
          borderRadius: "50%",
          backgroundColor: colorsFoundation.neutral[100],
        },
      },
    },
  },
  MuiCssBaseline: {
    styleOverrides: {
      "*:focus-visible": {
        outline: `2px solid ${colorsFoundation.key.blue[700]}`,
        outlineOffset: "2px",
      },
      "@media (prefers-reduced-motion: reduce)": {
        "*, *::before, *::after": {
          transitionDuration: "0.001ms !important",
          animationDuration: "0.001ms !important",
        },
      },
    },
  },
};

const portalThemes = {
  [PORTAL.SCHADENPORTAL]: createTheme(muiThemeV2, {
    palette: sidebarPalettes[PORTAL.SCHADENPORTAL],
  }),
  [PORTAL.KUNDENPORTAL]: createTheme(muiThemeV2, {
    palette: sidebarPalettes[PORTAL.KUNDENPORTAL],
    components: interactionComponents,
  }),
};

export function getPortalTheme(portal = PORTAL.SCHADENPORTAL) {
  return portalThemes[portal] ?? portalThemes[PORTAL.SCHADENPORTAL];
}
